import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { log } from "@/lib/log";
import { getStatePreset, PRESET_KEYS } from "@/lib/statePresets";
import { Sparkles, X, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * EmployeeFormModal — orchestrator only.
 *
 * The form used to be a 391-line monolith. Following the code-review
 * "Modal Cleanup" recommendation, the form body is now split into five
 * small, named sub-components that each render one logical section of
 * the modal. Everything stays in this file so callers still see a
 * single `<EmployeeFormModal>` API — the sub-components live below the
 * default export and share the parent's form-state via props.
 *
 *   • IdentitySection      — First / Last / Email / SSN / Filing / Hire
 *   • AddressSection       — Address / City / State / ZIP / Employment / Rate / Worksite
 *   • PortalLoginSection   — Portal role + password (create mode only)
 *   • PaymentMethodSection — Check / Direct Deposit / PayPal
 *   • TaxRatesSection      — SUI / SDI / Local / 401k / §125 + state preset
 *   • BenefitsSection      — Vacation + sick accrual + balances
 */

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const NUMERIC_FIELDS = ["hourly_rate", "annual_salary", "additional_withholding", "federal_allowances", "sick_accrual_per_period", "vacation_accrual_per_period", "sick_balance_hours", "vacation_balance_hours", "dependents_amount", "other_income", "deductions_amount", "state_allowances"];
// Percent-typed fields (stored as decimals). Blank means "leave unchanged".
const PCT_FIELDS = ["sui_rate", "sdi_rate", "local_tax_rate", "k401_pct", "section125_pct"];

export default function EmployeeFormModal({ employee, onClose, onSaved }) {
  const [worksites, setWorksites] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/worksites");
        setWorksites(data);
      } catch (err) {
        log.warn("Failed to load worksites for employee form:", err?.message || err);
      }
    })();
  }, []);

  const isEdit = Boolean(employee);
  const [form, setForm] = useState(() => initialFormState(employee));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upd = (k) => (e) => {
    const v = e.target.value;
    // A cleared numeric field stays "" — it is dropped on save so the stored
    // value is preserved. Typing 0 is still an explicit "set to zero".
    const next = NUMERIC_FIELDS.includes(k) ? (v === "" ? "" : Number(v)) : v;
    setForm({ ...form, [k]: next });
  };
  // Percent-typed fields are shown to admins as 1.1 (%) but stored as 0.011.
  const pctUpd = (k) => (e) => {
    const v = e.target.value;
    if (v === "") { setForm({ ...form, [k]: "" }); return; }
    const num = Number(v);
    setForm({ ...form, [k]: Number.isFinite(num) ? num / 100 : "" });
  };
  const pctVal = (v) => {
    if (v === "" || v === null || v === undefined) return "";
    const n = Number(v) * 100;
    // Trim floating-point noise (e.g. 1.1000000000000001 → 1.1) without
    // forcing a fixed decimal, so admins can still type 0.75 etc.
    return Math.round(n * 10000) / 10000;
  };

  // Preset suggestions for the currently selected state.
  const preset = useMemo(() => getStatePreset(form.state), [form.state]);
  const presetAlreadyApplied = useMemo(() => {
    if (!preset) return false;
    return PRESET_KEYS.every((k) => Number(form[k] || 0).toFixed(6) === Number(preset[k] || 0).toFixed(6));  }, [preset, form]);
  const applyStatePreset = () => {
    if (!preset) return;
    setForm((f) => ({ ...f, ...Object.fromEntries(PRESET_KEYS.map((k) => [k, preset[k]])) }));
    toast.success(`Applied ${form.state} tax preset`);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // Drop UI-only helper fields so the API only receives the schema it expects.
      const payload = { ...form };
      delete payload.ssn_masked;
      // Blank numeric / percent fields mean "no change" — omit them entirely
      // so the backend's partial update never resets a stored rate to zero.
      for (const k of [...NUMERIC_FIELDS, ...PCT_FIELDS]) {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined || Number.isNaN(payload[k])) {
          delete payload[k];
        }
      }
      // Empty SSN on edit means "keep existing"; drop it so we don't nuke the value.
      if (isEdit && !payload.ssn) delete payload.ssn;
      const url = isEdit ? `/employees/${employee.id}` : "/employees";
      const { data } = isEdit ? await api.put(url, payload) : await api.post(url, payload);
      toast.success(isEdit ? "Employee updated" : "Employee added");
      onSaved(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  // Portalled to <body>: the dashboard's main column is its own stacking
  // context, so a z-50 overlay rendered inside it still lost to the sticky
  // z-40 sidebar and the modal came out sliced down its left edge on tablet
  // and desktop widths.
  return createPortal(
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border-2 border-ink w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-hard-lg">
        <div className="flex items-center justify-between border-b-2 border-ink px-6 py-4 sticky top-0 bg-white z-10">
          <div>
            <div className="overline">§ EMPLOYEE</div>
            <h2 className="font-heading font-black text-2xl">{isEdit ? "Edit Employee" : "Add Employee"}</h2>
          </div>
          <button onClick={onClose} data-testid="close-employee-modal" className="p-2 border-2 border-ink hover:bg-ink hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {/* Stays two columns at every width: the sections below use
            `col-span-2` headers, and a single-column grid would make those
            spans invent a second implicit column that collapses every label
            to ~28px. */}
        <form onSubmit={save} className="p-6 grid grid-cols-2 gap-4">
          <IdentitySection form={form} setForm={setForm} upd={upd} isEdit={isEdit} />
          <ComplianceSection form={form} setForm={setForm} upd={upd} />
          <AddressSection form={form} upd={upd} worksites={worksites} />
          {!isEdit && <PortalLoginSection form={form} upd={upd} />}
          <PaymentMethodSection form={form} upd={upd} isEdit={isEdit} employee={employee} />
          <TaxRatesSection
            form={form} setForm={setForm} updRaw={upd} pctUpd={pctUpd} pctVal={pctVal}
            preset={preset} presetAlreadyApplied={presetAlreadyApplied}
            applyStatePreset={applyStatePreset}
          />
          <BenefitsSection form={form} upd={upd} isEdit={isEdit} />
          {err && <div className="col-span-2 border-2 border-bad text-bad font-mono text-xs p-3">{err}</div>}
          <div className="col-span-2 flex justify-end gap-3 pt-4 border-t-2 border-ink">
            <button type="button" onClick={onClose} className="px-6 py-3 border-2 border-ink font-mono uppercase text-xs tracking-widest hover:bg-secondary">Cancel</button>
            <button data-testid="save-employee" disabled={busy} type="submit" className="hard-btn px-6 py-3 font-mono uppercase text-xs tracking-widest">
              {busy ? "Saving…" : (isEdit ? "Update Employee" : "Add Employee")}
            </button>
          </div>
        </form>
      </div>
      <style>{`.input{width:100%;padding:0.65rem 0.75rem;border:2px solid #0A0A0A;font-family:"IBM Plex Mono",monospace;font-size:0.85rem;background:#fff}`}</style>
    </div>,
    document.body,
  );
}

// -------------------- Form-state initializer --------------------
function initialFormState(employee) {
  return {
    first_name: employee?.first_name || "",
    middle_name: employee?.middle_name || "",
    last_name: employee?.last_name || "",
    email: employee?.email || "",
    phone: employee?.phone || "",
    date_of_birth: employee?.date_of_birth || "",
    ssn: "",  // full 9-digit tax ID entered by admin, sent as string on save
    business_name: employee?.business_name || "",
    tin_type: employee?.tin_type || "ssn",
    entity_type: employee?.entity_type || "",
    ssn_masked: employee?.ssn_masked || "",  // read-only display of previously stored SSN
    tax_filing_type: employee?.tax_filing_type || "w2",
    address: employee?.address || "",
    city: employee?.city || "",
    state: employee?.state || "CA",
    zip_code: employee?.zip_code || "",
    // Emergency contact
    emergency_contact_name: employee?.emergency_contact_name || "",
    emergency_contact_phone: employee?.emergency_contact_phone || "",
    emergency_contact_relationship: employee?.emergency_contact_relationship || "",
    employment_type: employee?.employment_type || "hourly",
    hourly_rate: employee?.hourly_rate || 0,
    annual_salary: employee?.annual_salary || 0,
    filing_status: employee?.filing_status || "single",
    federal_allowances: employee?.federal_allowances || 0,
    additional_withholding: employee?.additional_withholding || 0,
    // Modern (2020+) W-4
    dependents_amount: employee?.dependents_amount || 0,
    other_income: employee?.other_income || 0,
    deductions_amount: employee?.deductions_amount || 0,
    multiple_jobs: !!employee?.multiple_jobs,
    tax_exempt: !!employee?.tax_exempt,
    // State-level
    state_filing_status: employee?.state_filing_status || "",
    state_allowances: employee?.state_allowances || 0,
    sui_rate: employee?.sui_rate || 0,
    sdi_rate: employee?.sdi_rate || 0,
    local_tax_rate: employee?.local_tax_rate || 0,
    local_jurisdiction: employee?.local_jurisdiction || "",
    ny_pfl_optout: !!employee?.ny_pfl_optout,
    ny_supplemental_flat: !!employee?.ny_supplemental_flat,
    k401_pct: employee?.k401_pct || 0,
    section125_pct: employee?.section125_pct || 0,
    status: employee?.status || "active",
    hire_date: employee?.hire_date || new Date().toISOString().slice(0, 10),
    login_role: employee?.login_role || "employee",
    login_password: "",
    payment_method: employee?.payment_method || "check",
    bank_routing: "",
    bank_account: "",
    paypal_email: employee?.paypal_email || "",
    worksite_id: employee?.worksite_id || "",
    sick_accrual_per_period: employee?.sick_accrual_per_period ?? 0,
    sick_accrual_mode: employee?.sick_accrual_mode || "auto",
    vacation_accrual_per_period: employee?.vacation_accrual_per_period ?? 0,
    sick_balance_hours: employee?.sick_balance_hours ?? 0,
    vacation_balance_hours: employee?.vacation_balance_hours ?? 0,
  };
}

// -------------------- Section components --------------------
function IdentitySection({ form, setForm, upd, isEdit }) {
  const [showSsn, setShowSsn] = useState(false);
  const onSsnChange = (e) => {
    // Auto-format while typing: XX-XXXXXXX for a business EIN, otherwise
    // XXX-XX-XXXX. Non-digits are stripped on save either way.
    const d = e.target.value.replace(/\D/g, "").slice(0, 9);
    let out = d;
    if (form.tax_filing_type === "1099" && form.tin_type === "ein") {
      if (d.length > 2) out = `${d.slice(0, 2)}-${d.slice(2)}`;
    } else if (d.length > 5) out = `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
    else if (d.length > 3) out = `${d.slice(0,3)}-${d.slice(3)}`;
    setForm({ ...form, ssn: out });
  };
  const digits = (form.ssn || "").replace(/\D/g, "");
  const is1099 = form.tax_filing_type === "1099";
  const isEin = is1099 && form.tin_type === "ein";
  const idLabel = isEin ? "Business EIN" : "SSN or ITIN";
  const itin = !isEin && digits.length === 9 && digits.startsWith("9");
  const itinOnW2 = itin && form.tax_filing_type === "w2";
  return (
    <>
      <Field label="First Name"><input required data-testid="emp-first-name" value={form.first_name} onChange={upd("first_name")} className="input" /></Field>
      <Field label="Middle Name"><input data-testid="emp-middle-name" value={form.middle_name} onChange={upd("middle_name")} placeholder="Initial used on W-2 & NYS-45" className="input" /></Field>
      <Field label="Last Name"><input required data-testid="emp-last-name" value={form.last_name} onChange={upd("last_name")} className="input" /></Field>
      <Field label="Email"><input type="email" required data-testid="emp-email" value={form.email} onChange={upd("email")} className="input" /></Field>
      <Field label={isEdit && form.ssn_masked
        ? `${idLabel} (on file: ${form.ssn_masked})`
        : `${idLabel} (9 digits, encrypted at rest)`}>
        <div className="flex items-stretch gap-2">
          <input
            type={showSsn ? "text" : "password"}
            inputMode="numeric" maxLength={11}
            autoComplete="off"
            placeholder={isEdit ? "Leave blank to keep existing" : "123-45-6789"}
            data-testid="emp-ssn" value={form.ssn} onChange={onSsnChange}
            className="input flex-1"
          />
          <button
            type="button"
            data-testid="emp-ssn-toggle"
            aria-label={showSsn ? "Hide tax ID" : "Show tax ID"}
            aria-pressed={showSsn}
            onClick={() => setShowSsn((v) => !v)}
            className="border-2 border-ink px-3 hover:bg-ink hover:text-white transition-colors"
          >
            {showSsn ? <EyeOff className="w-4 h-4 pointer-events-none" /> : <Eye className="w-4 h-4 pointer-events-none" />}
          </button>
        </div>
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
          {isEin
            ? "Federal EIN of the business being paid — prints as XX-XXXXXXX in the 1099-NEC recipient TIN box."
            : "SSN for W-2 workers; an IRS ITIN (begins with 9) is accepted for 1099 contractors."}
          {" "}Stored Fernet-encrypted; only last-4 is ever shown. Showing it is a local view only —
          nothing unmasked is stored or logged.
        </div>
        {itinOnW2 && (
          <div data-testid="emp-itin-w2-warning"
               className="mt-2 border-2 border-ink bg-warn p-2 font-mono text-[10px] flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>That is an ITIN. An ITIN cannot be used for W-2 wage reporting — set Tax Filing Type to 1099, or enter an SSN.</span>
          </div>
        )}
      </Field>
      <Field label="Tax Filing Type">
        <select data-testid="emp-tax-filing-type" value={form.tax_filing_type} onChange={upd("tax_filing_type")} className="input">
          <option value="w2">W-2 (Employee)</option>
          <option value="1099">1099 (Contractor)</option>
        </select>
        {itin && (
          <div className="mt-1 font-mono text-[10px] text-muted-foreground" data-testid="emp-itin-note">
            ITIN detected — this worker must file on a 1099.
          </div>
        )}
      </Field>

      {is1099 && (
        <>
          <Field label="Contractor Type">
            <select
              data-testid="emp-tin-type"
              value={form.tin_type}
              onChange={(e) => setForm({ ...form, tin_type: e.target.value, ssn: "" })}
              className="input"
            >
              <option value="ssn">Individual — SSN or ITIN</option>
              <option value="ein">Business — EIN</option>
            </select>
            <div className="mt-1 font-mono text-[10px] text-muted-foreground">
              Sets which tax ID is valid and how it prints on the 1099-NEC:
              {" "}{isEin ? "XX-XXXXXXX" : "XXX-XX-XXXX"}.
            </div>
          </Field>
          <Field label={isEin ? "Business Name (legal name on the 1099)" : "Business Name (optional)"}>
            <input
              data-testid="emp-business-name"
              value={form.business_name}
              onChange={upd("business_name")}
              placeholder={isEin ? "Acme Consulting LLC" : "Trading name, if any"}
              className="input"
            />
            <div className="mt-1 font-mono text-[10px] text-muted-foreground">
              When set, this is the name printed in the recipient box instead of the individual's.
            </div>
          </Field>
          <Field label="Entity Type (internal record)">
            <select data-testid="emp-entity-type" value={form.entity_type} onChange={upd("entity_type")} className="input">
              <option value="">— Not recorded —</option>
              <option value="sole_prop">Sole Proprietor</option>
              <option value="single_llc">Single-member LLC</option>
              <option value="llc">LLC</option>
              <option value="s_corp">S-Corp</option>
              <option value="c_corp">C-Corp</option>
              <option value="partnership">Partnership</option>
              <option value="nonprofit">Non-profit</option>
            </select>
            <div className="mt-1 font-mono text-[10px] text-muted-foreground">
              Kept for your own records and the bulk-export manifest. The 1099-NEC itself has no
              entity-type box.
            </div>
          </Field>
        </>
      )}
      <Field label="Hire Date">
        <input type="date" data-testid="emp-hire-date" value={form.hire_date} onChange={upd("hire_date")} className="input" />
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">Starts the 90-day probation clock for HR-doc access.</div>
      </Field>
    </>
  );
}

function ComplianceSection({ form, setForm, upd }) {
  const chk = (k) => (e) => setForm({ ...form, [k]: e.target.checked });
  return (
    <>
      <div className="col-span-2 mt-2 border-t-2 border-ink pt-4">
        <div className="overline mb-3">§ DEMOGRAPHICS · EMERGENCY CONTACT</div>
      </div>
      <Field label="Phone"><input data-testid="emp-phone" type="tel" placeholder="(555) 555-1234" value={form.phone} onChange={upd("phone")} className="input" /></Field>
      <Field label="Date of Birth"><input data-testid="emp-dob" type="date" value={form.date_of_birth} onChange={upd("date_of_birth")} className="input" /></Field>
      <Field label="Emergency Contact Name"><input data-testid="emp-ec-name" value={form.emergency_contact_name} onChange={upd("emergency_contact_name")} className="input" /></Field>
      <Field label="Emergency Contact Phone"><input data-testid="emp-ec-phone" type="tel" value={form.emergency_contact_phone} onChange={upd("emergency_contact_phone")} className="input" /></Field>
      <Field label="Relationship"><input data-testid="emp-ec-rel" placeholder="Spouse / Parent / Sibling" value={form.emergency_contact_relationship} onChange={upd("emergency_contact_relationship")} className="input" /></Field>

      <div className="col-span-2 mt-4 border-t-2 border-ink pt-4">
        <div className="overline mb-2">§ MODERN W-4 (2020+) · IRS PUB 15-T</div>
        <div className="font-mono text-[10px] text-muted-foreground mb-3">These fields drive federal withholding on every paycheck. Leave zero for pre-2020 W-4 employees — the legacy Filing Status + Additional Withholding fields still apply.</div>
      </div>
      <Field label="Step 3 · Dependents Amount ($ annual credit)">
        <input data-testid="emp-w4-dependents" type="number" step="0.01" value={form.dependents_amount} onChange={upd("dependents_amount")} className="input" />
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">$2,000/child + $500/other. Reduces annual federal tax.</div>
      </Field>
      <Field label="Step 4a · Other Income ($ annual)">
        <input data-testid="emp-w4-other-income" type="number" step="0.01" value={form.other_income} onChange={upd("other_income")} className="input" />
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">Non-wage income to withhold on (investments, etc.)</div>
      </Field>
      <Field label="Step 4b · Deductions ($ annual, above standard)">
        <input data-testid="emp-w4-deductions" type="number" step="0.01" value={form.deductions_amount} onChange={upd("deductions_amount")} className="input" />
      </Field>
      <Field label="Additional Federal Withholding ($/period)"><input type="number" step="0.01" data-testid="emp-w4-additional" value={form.additional_withholding} onChange={upd("additional_withholding")} className="input" /></Field>
      <div className="col-span-2 flex flex-wrap gap-6 pt-2">
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="emp-w4-multi-jobs" checked={form.multiple_jobs} onChange={chk("multiple_jobs")} className="w-5 h-5 accent-ink" />
          <div>
            <div className="font-mono text-sm font-black">Step 2(c) · Multiple Jobs / Two-Earner Household</div>
            <div className="font-mono text-[10px] text-muted-foreground">Forces the higher single-rate brackets even when married-filing-jointly.</div>
          </div>
        </label>
        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" data-testid="emp-w4-exempt" checked={form.tax_exempt} onChange={chk("tax_exempt")} className="w-5 h-5 accent-bad" />
          <div>
            <div className="font-mono text-sm font-black text-bad">Line 4c · Tax Exempt (writes "Exempt" on W-4)</div>
            <div className="font-mono text-[10px] text-muted-foreground">Zero federal income tax withholding. FICA (SS + Medicare) still applies.</div>
          </div>
        </label>
      </div>

      <div className="col-span-2 mt-4 border-t-2 border-ink pt-4">
        <div className="overline mb-3">§ STATE FILING (SEPARATE FROM FEDERAL)</div>
      </div>
      <Field label="State Filing Status">
        <select data-testid="emp-state-filing" value={form.state_filing_status || ""} onChange={upd("state_filing_status")} className="input">
          <option value="">— Use federal filing status —</option>
          <option value="single">Single</option>
          <option value="married">Married Filing Jointly</option>
          <option value="married_separately">Married Filing Separately</option>
          <option value="head">Head of Household</option>
        </select>
      </Field>
      <Field label="State Allowances">
        <input data-testid="emp-state-allowances" type="number" min="0" value={form.state_allowances} onChange={upd("state_allowances")} className="input" />
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">CA DE 4, NY IT-2104, etc.</div>
      </Field>

      <div className="col-span-2 mt-4 border-t-2 border-ink pt-4">
        <div className="overline mb-2 flex items-center gap-2">⚖ § STATUTORY WITHHOLDINGS · COURT ORDERS
          <span className="ml-2 px-1.5 py-0.5 bg-warn text-ink text-[9px] font-black uppercase tracking-widest">Enterprise-Grade</span>
        </div>
        <div className="font-mono text-[10px] text-muted-foreground mb-3">Child support, IRS levies, creditor garnishments, and student-loan wage garnishments. Applied AFTER taxes, BEFORE net pay. `cap_per_period` respects CCPA 25%/50% federal caps.</div>
        <GarnishmentsRepeater form={form} setForm={setForm} />
      </div>
    </>
  );
}

function GarnishmentsRepeater({ form, setForm }) {
  const orders = form.garnishments || [];
  const upd = (idx, patch) => {
    const next = orders.map((o, i) => i === idx ? { ...o, ...patch } : o);
    setForm({ ...form, garnishments: next });
  };
  const add = () => setForm({ ...form, garnishments: [...orders, { name: "", type: "fixed", amount: 0, cap_per_period: 0, case_number: "", active: true }] });
  const remove = (idx) => setForm({ ...form, garnishments: orders.filter((_, i) => i !== idx) });
  return (
    <div className="col-span-2 space-y-3" data-testid="garnishments-repeater">
      {orders.length === 0 && (
        <div className="font-mono text-xs text-muted-foreground italic">No court orders on file.</div>
      )}
      {orders.map((g, i) => (
        <div key={i} className={`border-2 ${g.active === false ? "border-muted opacity-60" : "border-warn"} p-3 space-y-2`} data-testid={`garnishment-row-${i}`}>
          <div className="grid grid-cols-6 gap-2">
            <div className="col-span-2">
              <div className="overline mb-1">Order Name</div>
              <input data-testid={`garn-name-${i}`} value={g.name || ""} onChange={(e) => upd(i, { name: e.target.value })} placeholder="Child Support · Alameda County" className="input w-full" />
            </div>
            <div>
              <div className="overline mb-1">Type</div>
              <select data-testid={`garn-type-${i}`} value={g.type || "fixed"} onChange={(e) => upd(i, { type: e.target.value })} className="input w-full">
                <option value="fixed">Fixed $</option>
                <option value="percent">% Disposable</option>
              </select>
            </div>
            <div>
              <div className="overline mb-1">{g.type === "percent" ? "% (0.25 = 25%)" : "Amount $"}</div>
              <input data-testid={`garn-amt-${i}`} type="number" step="0.01" value={g.amount || 0} onChange={(e) => upd(i, { amount: Number(e.target.value) })} className="input w-full" />
            </div>
            <div>
              <div className="overline mb-1">Cap $/period</div>
              <input data-testid={`garn-cap-${i}`} type="number" step="0.01" value={g.cap_per_period || 0} onChange={(e) => upd(i, { cap_per_period: Number(e.target.value) })} className="input w-full" />
            </div>
            <div>
              <div className="overline mb-1">Case #</div>
              <input data-testid={`garn-case-${i}`} value={g.case_number || ""} onChange={(e) => upd(i, { case_number: e.target.value })} className="input w-full" placeholder="SF-2019-CS-1234" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 font-mono text-xs cursor-pointer">
              <input type="checkbox" data-testid={`garn-active-${i}`} checked={g.active !== false} onChange={(e) => upd(i, { active: e.target.checked })} />
              Active — deduct on next payroll
            </label>
            <button type="button" onClick={() => remove(i)} data-testid={`garn-remove-${i}`} className="border-2 border-bad text-bad px-2 py-1 font-mono uppercase text-[10px] tracking-widest hover:bg-bad hover:text-white">Remove ×</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} data-testid="garn-add" className="border-2 border-ink px-3 py-1.5 font-mono uppercase text-[10px] tracking-widest hover:bg-ink hover:text-white">+ Add Court Order</button>
    </div>
  );
}

function AddressSection({ form, upd, worksites }) {
  return (
    <>
      <Field label="Address" className="col-span-2"><input data-testid="emp-address" value={form.address} onChange={upd("address")} className="input" /></Field>
      <Field label="City"><input value={form.city} onChange={upd("city")} className="input" /></Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="State"><select data-testid="emp-state" value={form.state} onChange={upd("state")} className="input">{STATES.map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="ZIP"><input value={form.zip_code} onChange={upd("zip_code")} className="input" /></Field>
      </div>
      <Field label="Employment Type">
        <select data-testid="emp-type" value={form.employment_type} onChange={upd("employment_type")} className="input">
          <option value="hourly">Hourly</option>
          <option value="salary">Salary</option>
        </select>
      </Field>
      {form.employment_type === "hourly" ? (
        <Field label="Hourly Rate ($)"><input type="number" step="0.01" data-testid="emp-hourly-rate" value={form.hourly_rate} onChange={upd("hourly_rate")} className="input" /></Field>
      ) : (
        <Field label="Annual Salary ($)"><input type="number" step="0.01" data-testid="emp-salary" value={form.annual_salary} onChange={upd("annual_salary")} className="input" /></Field>
      )}
      <Field label="Filing Status">
        <select data-testid="emp-filing" value={form.filing_status} onChange={upd("filing_status")} className="input">
          <option value="single">Single</option>
          <option value="married">Married</option>
          <option value="head">Head of Household</option>
        </select>
      </Field>
      <Field label="Additional Federal Withholding ($/period)"><input type="number" step="0.01" value={form.additional_withholding} onChange={upd("additional_withholding")} className="input" /></Field>
      <Field label="Status">
        <select value={form.status} onChange={upd("status")} className="input">
          <option value="active">Active</option>
          <option value="terminated">Terminated</option>
        </select>
      </Field>
      <Field label="Assigned Worksite" className="col-span-2">
        <select data-testid="emp-worksite" value={form.worksite_id || ""} onChange={upd("worksite_id")} className="input">
          <option value="">— No worksite (all clock-ins flagged) —</option>
          {worksites.map((w) => <option key={w.id} value={w.id}>{`${w.name} · ${w.city} ${w.state} (radius ${w.radius_meters}m)`}</option>)}
        </select>
      </Field>
    </>
  );
}

function PortalLoginSection({ form, upd }) {
  return (
    <>
      <Field label="Portal Role">
        <select data-testid="emp-login-role" value={form.login_role} onChange={upd("login_role")} className="input">
          <option value="employee">Employee — sees only their own portal</option>
          <option value="admin">Admin — full management dashboard</option>
        </select>
      </Field>
      <Field label="Employee Portal Password (optional)">
        <input type="password" data-testid="emp-login-password" placeholder="Leave blank to skip creating login" value={form.login_password} onChange={upd("login_password")} className="input" />
      </Field>
    </>
  );
}

function PaymentMethodSection({ form, upd, isEdit, employee }) {
  return (
    <>
      <div className="col-span-2 border-t-2 border-ink pt-4 mt-2">
        <div className="overline mb-3">§ PAYMENT METHOD</div>
      </div>
      <Field label="Payment Method" className="col-span-2">
        <select data-testid="emp-payment-method" value={form.payment_method} onChange={upd("payment_method")} className="input">
          <option value="check">Printed Check</option>
          <option value="direct_deposit">Direct Deposit (ACH)</option>
          <option value="paypal">PayPal Payout</option>
        </select>
      </Field>
      {form.payment_method === "direct_deposit" && (
        <Field label="Bank Details" className="col-span-2">
          <div className="border-2 border-ink p-3 font-mono text-[11px] leading-relaxed" data-testid="emp-bank-managed-notice">
            {employee?.bank_routing_on_file
              ? <>Account <b>••••{employee?.bank_account_last4 || "••••"}</b> · routing <b>•••••{employee?.bank_routing_last4 || ""}</b> on file.</>
              : <>No bank account on file yet.</>}
            <div className="text-muted-foreground mt-1">
              Routing and account numbers are entered and verified in the employee's{" "}
              <b>Payment Rails</b> panel (Employees → open the employee), so there is exactly one
              audited entry point.
            </div>
          </div>
        </Field>
      )}
      {form.payment_method === "paypal" && (
        <Field label="PayPal Email (receiver)" className="col-span-2">
          <input data-testid="emp-paypal-email" type="email" required value={form.paypal_email} onChange={upd("paypal_email")} placeholder="employee@example.com" className="input" />
          <div className="mt-1 font-mono text-[10px] text-muted-foreground">Payouts are sent via the PayPal Payouts API to this email. The recipient must have (or create) a PayPal account to claim funds.</div>
        </Field>
      )}
    </>
  );
}

function TaxRatesSection({ form, setForm, updRaw, pctUpd, pctVal, preset, presetAlreadyApplied, applyStatePreset }) {
  return (
    <>
      <div className="col-span-2 border-t-2 border-ink pt-4 mt-2">
        <div className="overline mb-1">§ STATE / LOCAL · PRE-TAX DEDUCTIONS</div>
        <div className="font-mono text-[10px] text-muted-foreground mb-3">
          Enter percentages (e.g. 1.1 for 1.1%). Leave 0 to skip a line on the stub.
          SUI/SDI/Local rates vary by state and municipality — consult your CPA.
        </div>
      </div>
      {preset && (
        <div
          data-testid="state-preset-hint"
          className="col-span-2 -mt-2 mb-1 border-2 border-ink bg-secondary/60 px-3 py-2 flex items-center justify-between gap-3"
        >
          <div className="font-mono text-[11px] leading-snug">
            <span className="font-bold">↳ {form.state} preset ·</span>{" "}
            SUI {pctVal(preset.sui_rate)}% · SDI {pctVal(preset.sdi_rate)}% · Local {pctVal(preset.local_tax_rate)}%
            <div className="text-[10px] text-muted-foreground mt-0.5">{preset.note}</div>
          </div>
          <button
            type="button" data-testid="apply-state-preset"
            onClick={applyStatePreset} disabled={presetAlreadyApplied}
            className="shrink-0 inline-flex items-center gap-1 border-2 border-ink px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-ink hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            title={presetAlreadyApplied ? "Already applied" : `Fill SUI/SDI/Local with ${form.state} defaults`}
          >
            <Sparkles className="w-3 h-3" />
            {presetAlreadyApplied ? "Applied" : "Apply →"}
          </button>
        </div>
      )}
      <PctField label="SUI Rate (%) — State Unemployment" testid="emp-sui-rate" k="sui_rate" form={form} pctUpd={pctUpd} pctVal={pctVal} />
      <PctField label="SDI Rate (%) — State Disability" testid="emp-sdi-rate" k="sdi_rate" form={form} pctUpd={pctUpd} pctVal={pctVal} />
      <PctField label="Local Income Tax Rate (%)" testid="emp-local-rate" k="local_tax_rate" form={form} pctUpd={pctUpd} pctVal={pctVal} />
      <Field label="Local Jurisdiction">
        <select data-testid="emp-local-jurisdiction" value={form.local_jurisdiction || ""} onChange={updRaw("local_jurisdiction")} className="input">
          <option value="">Use flat local rate above</option>
          <optgroup label="New York">
            <option value="nyc">New York City (3.876%)</option>
            <option value="yonkers_resident">Yonkers — resident (16.75% surcharge on NY tax)</option>
            <option value="yonkers_nonresident">Yonkers — non-resident (0.50% of wages)</option>
          </optgroup>
          <optgroup label="Pennsylvania">
            <option value="philadelphia_resident">Philadelphia — resident (3.75%)</option>
            <option value="philadelphia_nonresident">Philadelphia — non-resident (3.44%)</option>
            <option value="pittsburgh">Pittsburgh (3.00%)</option>
          </optgroup>
          <optgroup label="Ohio">
            <option value="columbus_oh">Columbus (2.50%)</option>
            <option value="cleveland_oh">Cleveland (2.50%)</option>
            <option value="cincinnati_oh">Cincinnati (1.80%)</option>
          </optgroup>
          <optgroup label="Kentucky">
            <option value="louisville_ky">Louisville (2.20%)</option>
            <option value="lexington_ky">Lexington (2.25%)</option>
          </optgroup>
          <optgroup label="Michigan">
            <option value="detroit_mi">Detroit (2.40%)</option>
            <option value="grand_rapids_mi">Grand Rapids (1.35%)</option>
          </optgroup>
          <optgroup label="Missouri">
            <option value="kansas_city_mo">Kansas City (1.00%)</option>
            <option value="st_louis_mo">St. Louis (1.00%)</option>
          </optgroup>
          <optgroup label="Maryland">
            <option value="baltimore_md">Baltimore City (3.20%)</option>
            <option value="montgomery_md">Montgomery County (3.20%)</option>
          </optgroup>
          <optgroup label="Other">
            <option value="marion_in">Marion County, IN (2.02%)</option>
            <option value="wilmington_de">Wilmington, DE (1.25%)</option>
          </optgroup>
        </select>
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
          Municipal rates are applied exactly and override the flat rate above. Yonkers residents are computed as a surcharge on the state tax per NYS-50-T-Y.
        </div>
      </Field>
      {form.state === "NY" && (
        <div className="col-span-2 border-2 border-ink bg-secondary/40 p-3 space-y-2" data-testid="ny-statutory-panel">
          <div className="font-mono text-[10px] font-black uppercase tracking-widest">NY Statutory Programs · 2026</div>
          <div className="font-mono text-[10px] text-muted-foreground leading-relaxed">
            PFL withheld at 0.432% of gross, capped at $411.91/yr (NYSAWW $1,833.63). DB/SDI at 0.5% capped at $0.60/week.
            2027–2028 PFL rates are <b>provisional</b> (2026 carried forward) until NY DFS publishes them.
          </div>
          <label className="flex items-center gap-2 font-mono text-[11px]">
            <input type="checkbox" data-testid="emp-pfl-optout" checked={!!form.ny_pfl_optout} onChange={(e) => setForm({ ...form, ny_pfl_optout: e.target.checked })} className="w-4 h-4 accent-ink" />
            Exempt from Paid Family Leave (rare — requires a filed waiver)
          </label>
          <label className="flex items-center gap-2 font-mono text-[11px]">
            <input type="checkbox" data-testid="emp-suppl-flat" checked={!!form.ny_supplemental_flat} onChange={(e) => setForm({ ...form, ny_supplemental_flat: e.target.checked })} className="w-4 h-4 accent-ink" />
            Withhold overtime &amp; bonuses at NY's flat 11.70% supplemental rate
          </label>
        </div>
      )}
      <PctField label="401(k) Contribution (% of gross)" testid="emp-k401-pct" k="k401_pct" form={form} pctUpd={pctUpd} pctVal={pctVal} step="0.1" />
      <Field label="Section 125 Cafeteria Plan (% of gross)" className="col-span-2">
        <input
          data-testid="emp-s125-pct" type="number" step="0.1" min="0" max="100"
          value={pctVal(form.section125_pct)} onChange={pctUpd("section125_pct")}
          className="input"
        />
        <div className="mt-1 font-mono text-[10px] text-muted-foreground">
          Section 125 is pre-tax for ALL taxes (federal, state, SS, Medicare).
          401(k) is pre-tax for federal + state income only.
        </div>
      </Field>
    </>
  );
}

function BenefitsSection({ form, upd, isEdit }) {
  return (
    <>
      <div className="col-span-2 border-t-2 border-ink pt-4 mt-2">
        <div className="overline mb-3">§ BENEFITS · SICK + VACATION</div>
      </div>
      <Field label="Vacation Accrual (hours per pay period)">
        <input data-testid="emp-vacation-accrual" type="number" step="0.01" min="0" value={form.vacation_accrual_per_period} onChange={upd("vacation_accrual_per_period")} className="input" />
      </Field>
      <Field label="Sick Accrual (hours per pay period)">
        <input data-testid="emp-sick-accrual" type="number" step="0.01" min="0" value={form.sick_accrual_per_period} onChange={upd("sick_accrual_per_period")} className="input" />
      </Field>
      {isEdit && (
        <>
          <Field label="Current Vacation Balance (hours)">
            <input data-testid="emp-vacation-balance" type="number" step="0.01" value={form.vacation_balance_hours} onChange={upd("vacation_balance_hours")} className="input" />
          </Field>
          <Field label="Current Sick Balance (hours)">
            <input data-testid="emp-sick-balance" type="number" step="0.01" value={form.sick_balance_hours} onChange={upd("sick_balance_hours")} className="input" />
          </Field>
        </>
      )}
    </>
  );
}

// -------------------- Tiny presentational helpers --------------------
function PctField({ label, testid, k, form, pctUpd, pctVal, step = "0.001" }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid} type="number" step={step} min="0" max="15"
        value={pctVal(form[k])} onChange={pctUpd(k)}
        className="input"
      />
    </Field>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="overline mb-2">{label}</div>
      {children}
    </label>
  );
}
